import React, { useEffect } from 'react';
import { localDayjs } from 'utils/dateTimeUtils';
import PropTypes from 'prop-types';
import './style.scss';
import Modal from '../Modal';
import { connect } from 'react-redux';
import { fromResource } from 'store/selectors';
import useWindowSize from 'hooks/useWindowSize';
import { fulfilled, pending } from 'redux-saga-thunk';
import {
  resourceCreateRequest,
  resourceDetailReadRequest,
} from 'store/actions';
import Spinner from 'components/atoms/Spinner';
import { BANNER_POLL_INTERVAL_MS } from 'constant';

SingleLineBanner.propTypes = {
  isBannerCrisis: PropTypes.bool,
  title: PropTypes.string,
  introduction: PropTypes.string,
  body: PropTypes.string,
  handleReadStatus: PropTypes.func,
  onOpenModal: PropTypes.func,
};

function SingleLineBanner({
  isBannerCrisis = false,
  title = '',
  introduction = '',
  body = '',
  handleReadStatus = () => { },
  onOpenModal = () => { },
}) {
  const { isMobileView } = useWindowSize();
  const bannerClass = isBannerCrisis ? 'banner__crisis' : 'banner__info';

  const bannerIcon = isBannerCrisis ? 'warning' : 'info';

  return (
    <div className={`banner__container ${bannerClass}`}>
      <div className="banner__details">
        <i className="banner__icon--desktop material-icons">{bannerIcon}</i>
        <div className="banner__information">
          <div className="banner__heading">
            <i className="banner__icon--mobile material-icons">{bannerIcon}</i>
            <h5>{title}</h5>
          </div>
          <div
            className="banner__description"
            onClick={e => {
              if (e.target.id === 'banner-read-more') {
                onOpenModal(body, title);
              }
            }}
            onKeyDown={e => {
              if (e.target.id === 'banner-read-more' && e.key === 'Enter') {
                e.preventDefault();
                onOpenModal(body, title);
              }
            }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html:
                  introduction
                    .replace(/^<div>|<\/div>$/gi, '')
                    .replace(/^<p>|<\/p>$/gi, '')
                    .trim() +
                  ' <span class="banner__more-info ml-2" tabindex="0" id="banner-read-more">Läs mer</span>',
              }}
            />
          </div>
        </div>
      </div>
      {!isBannerCrisis && (
        <div
          className="banner__close"
          onClick={handleReadStatus}
          tabIndex={0}
          onKeyDown={handleReadStatus}
        >
          {isMobileView ? (
            <i className="banner__icon--mobile material-icons">close</i>
          ) : (
            'Stäng meddelande'
          )}
        </div>
      )}
    </div>
  );
}

function Banner({
  banners = [],
  onReadBannerStatus = () => { },
  onGetBanners = () => { },
  isfulfilled = false,
  isPending = false,
}) {
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [selectedModalBody, setSelectedModalBody] = React.useState('');
  const [selectedModalTitle, setSelectedModalTitle] = React.useState('');

  const handleOpenModal = (body, title) => {
    setSelectedModalBody(body);
    setSelectedModalTitle(title);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedModalBody('');
    setSelectedModalTitle('');
  };

  useEffect(() => {
    if (isfulfilled) {
      onGetBanners();
    }
  }, [isfulfilled]);

  useEffect(() => {
    const pollIntervalHandle = setInterval(() => {
      onGetBanners();
    }, BANNER_POLL_INTERVAL_MS);

    return () => clearInterval(pollIntervalHandle);
  }, []);

  const lastHandledUnpublishRef = React.useRef(null);

  useEffect(() => {
    const visibleBanners = banners.filter(banner => !banner.IsRead);
    if (visibleBanners.length === 0) return;

    const unpublishTimes = visibleBanners
      .filter(banner => banner.DatetimeUnpublish)
      .map(banner =>
        localDayjs
          .tz(banner.DatetimeUnpublish, 'M/D/YYYY h:mm:ss A', 'Europe/Stockholm')
          .valueOf(),
      );

    if (unpublishTimes.length === 0) return;

    const earliestUnpublish = Math.min(...unpublishTimes);

    if (lastHandledUnpublishRef.current === earliestUnpublish) return;

    const now = Date.now();
    const delay = earliestUnpublish - now;

    if (delay <= 0) {
      lastHandledUnpublishRef.current = earliestUnpublish;
      onGetBanners();
      return;
    }

    const timerId = setTimeout(() => {
      lastHandledUnpublishRef.current = earliestUnpublish;
      onGetBanners();
    }, delay);

    return () => clearTimeout(timerId);
  }, [banners]);
  return (
    <>
      <div className="banner__layout">
        {banners
          .filter(banner => !banner.IsRead)
          .map(banner => (
            <SingleLineBanner
              isBannerCrisis={banner.IsBannerCrisis}
              title={banner.Title}
              introduction={banner.Introduction}
              body={banner.Body}
              handleReadStatus={() => {
                onReadBannerStatus({
                  BannerIdentifier: banner.BannerIdentifier,
                });
              }}
              onOpenModal={handleOpenModal}
              key={banner.BannerIdentifier}
            />
          ))}
      </div>
      {isPending && <Spinner />}
      {isModalVisible && (
        <Modal
          mainTitle={selectedModalTitle}
          isVisible={isModalVisible}
          className="tv-banner-modal__container"
          onClose={handleCloseModal}
        >
          <div className="tv-banner-modal__content">
            <p
              className="tv-banner-modal__introduction"
              dangerouslySetInnerHTML={{ __html: selectedModalBody }}
            />
          </div>
        </Modal>
      )}
    </>
  );
}

Banner.propTypes = {
  banners: PropTypes.array,
  isfulfilled: PropTypes.bool,
  isPending: PropTypes.bool,
  onReadBannerStatus: PropTypes.func,
  onGetBanners: PropTypes.func,
};

const mapStateToProps = state => {
  const banners = fromResource.getDetail(state, 'getBanners') || [];
  const isPending = pending(state, 'setBannerReadStatusCreate');
  const isfulfilled = fulfilled(state, 'setBannerReadStatusCreate');
  return {
    banners: banners.Banners || [],
    isPending,
    isfulfilled,
  };
};

const mapDispatchToProps = dispatch => ({
  onReadBannerStatus: data =>
    dispatch(resourceCreateRequest('setBannerReadStatus', data)),
  onGetBanners: () => dispatch(resourceDetailReadRequest('getBanners')),
});
export default connect(mapStateToProps, mapDispatchToProps)(Banner);
